import { ThemeConfig } from 'antd'
import tinycolor from 'tinycolor2'

export const lightenRate = 10
export const darkenRate = 15
export const disableRate = 0.5

export const colorPrimary = '#004B91'
export const colorSecondary = '#000000'

// common color
export const colorBlack = '#000000'
export const colorWhite = '#FFFFFF'
export const success = '#2ACF58'
export const error = '#D92440'
export const warning = '#FFA031'
export const info = '#3CA9E5'

// text colors
export const colorText = '#000000' // Use for primary text, such as body copy, sentence case headers, and buttons.
export const colorTextSubtle = '#46515A' // Use for primary text, such as body copy, sentence case headers, and buttons.
export const colorTextPlaceholder = '#6F787F' // Use for placehoder in form
export const colorTextSubtlest = '#999FA4' // Use for tertiary text, such as meta-data, breadcrumbs, input field placeholder and helper text.
export const colorTextDisable = '#ADB2B6' // Use for text in a disabled state.
export const colorTextGray = '#F5F5F6'
export const colorTextGray1 = '#EAEBEC'
export const colorTextDiscovery = '#883EE8' // Use for text to emphasize change or something new, such as in new lozenges.
export const colorTextSelected = '#004483' // Use for text in selected or opened states, such as tabs and dropdown buttons.
export const colorTextInverse = '#FFFFFF' // Use for text on bold backgrounds.

// border colors
export const colorBorder = '#ADB2B6' // Use to visually group or separate UI elements, such as flat cards or side panel dividers.
export const colorBorderSelected = '#003C74' //Use for borders or visual indicators of elements in a selected or opened state, such as in tabs or menu items.
export const colorBorderBold = '#6F787F' // A neutral border option that passes min 3:1 contrast ratios.
export const colorBorderInfo = '#43BBFF' // Use to visually group or separate UI elements, such as flat cards or side panel dividers.
export const colorBorderDisabled = '#D6D8DA' // Use for borders of elements in a disabled state.

// icon
export const colorIconDisplay = '#46515A' // color-icon-display

// background colors
export const colorBgDefault = '#FFFFFF'
export const colorBgBrandless = '#F3F6F9'
export const colorBgDark = '#000000'
export const colorBgSelect = '#E8EDF4'
export const colorBgPress = '#CCDBE9'
export const colorBgAccentGraySubtlest = '#F5F5F6'
export const colorBgAccentGraySubtlestPressed = '#D6D8DA'
export const colorBgGray1 = '#C1C5C8'
export const colorBgGray2 = '#999FA4'
export const colorBgGray3 = '#46515A'

export const colorBgRow = '#F5F5F6'

export const colorBgError = '#FDF3F5'
export const colorBgWarning = '#FFFAF4'
export const colorBgSuccess = '#F4FCF6'
export const colorBgInfo = '#F5FBFF'

export const colorBgErrorHovered = '#FCE8EA'
export const colorBgWarningHovered = '#FFF6EB'
export const colorBgSuccessHovered = '#EAFAEE'
export const colorBgInfoHovered = '#F5FBFF'

export const colorBgErrorBold = '#E11D34'
export const colorBgSucessBold = '#2ACF58'
export const colorBgWarningBold = '#FFA031'
export const colorBgInfoBold = '#43BBFF'

// For Button
export const colorBgButtonBold = 'linear-gradient(to bottom, #004B91, #334157) !important'
export const colorBgButtonDisable = 'linear-gradient(to bottom, #99B7D3, #ADB3BC) !important'
export const colorBgButtonSubtle = '#ADB2B6'
export const colorBgButtonSubtleHovered = '#EAEBEC'
export const colorBgButtonSubtlePressed = '#F5F5F6'

// Inherit all SeedToken and MapToken properties
export const lineWidthFocus = 1
const primaryThemeDefault: ThemeConfig = {
  token: {
    fontSize: 14,
    colorPrimary: colorPrimary,
    colorPrimaryHover: tinycolor(colorPrimary).darken(darkenRate).toHexString(),
    lineWidthFocus: lineWidthFocus,
    fontFamily: 'Myriad Pro, sans-serif',
    fontSizeLG: 16,
    fontSizeSM: 14,

    colorLink: colorTextSelected,
    colorText: colorText,

    controlHeightLG: 44,
    controlHeightSM: 32,
    controlHeightXS: 24,

    colorError: error,
    colorSuccess: success,
    colorWarning: warning,
    colorInfo: info,

    colorErrorText: error,
    colorSuccessText: success,
    colorWarningText: warning,
    colorInfoText: info,

    colorBgContainerDisabled: colorBgBrandless,
    colorTextDisabled: colorTextDisable,
    colorTextPlaceholder: colorTextPlaceholder,

    colorBorder: colorBorder,
    colorIcon: colorIconDisplay,
  },
  components: {
    Button: {
      colorPrimary: colorBgButtonBold,
      colorPrimaryHover: `linear-gradient(to bottom, #004482, #2D3B4E) !important`,
      colorPrimaryActive: `linear-gradient(to bottom, #003C73, #283446) !important`,
      colorBgContainerDisabled: colorBgButtonDisable,
      borderColorDisabled: colorBgButtonDisable,
      colorTextDisabled: colorWhite,
      borderRadiusLG: 8,
      borderRadiusSM: 6,
      borderRadiusXS: 4,

      defaultGhostBorderColor: colorPrimary,
      defaultGhostColor: colorPrimary,
    },

    Typography: {
      colorError: error,
      colorErrorActive: tinycolor(error).darken(darkenRate).toHexString(),
      colorLink: colorTextSelected,
      fontSize: 16,
      fontSizeHeading1: 24,
      fontSizeHeading2: 22,
      fontSizeHeading3: 18,
      fontSizeHeading4: 16,
      colorTextDescription: colorTextSubtle,
      fontWeightStrong: 700,
    },
    Input: {
      borderRadiusLG: 12,
      borderRadiusSM: 8,
      borderRadiusXS: 2,
    },
    InputNumber: {
      borderRadiusLG: 12,
      borderRadiusSM: 8,
      borderRadiusXS: 2,
    },
    Select: {
      fontSizeLG: 16,
      fontSizeSM: 14,

      controlHeightLG: 44,
      controlHeightSM: 32,
      controlHeightXS: 24,
      borderRadiusLG: 12,
      borderRadiusSM: 8,
      borderRadiusXS: 2,
    },
    Modal: {
      titleColor: colorPrimary,
      paddingContentHorizontal: 30,
    },
    Table: {
      colorTextHeading: colorTextGray,
    },
    Tooltip: {
      colorBgSpotlight: colorWhite,
      colorTextLightSolid: colorTextSubtle,
      borderRadius: 12,
    },
    Alert: {
      borderRadiusLG: 16,
      paddingContentHorizontalLG: 16,
      paddingContentVerticalLG: 16,
      colorErrorBorder: error,
      colorSuccessBorder: success,
      colorWarningBorder: warning,
      colorInfoBorder: info,

      colorErrorBg: colorBgError,
      colorSuccessBg: colorBgSuccess,
      colorWarningBg: colorBgWarning,
      colorInfoBg: colorBgInfo,

      fontSizeIcon: 18,
    },
    Tag: {
      borderRadiusSM: 12,
      colorErrorBorder: error,
      colorSuccessBorder: success,
      colorWarningBorder: warning,
      colorInfoBorder: info,

      colorErrorBg: colorBgError,
      colorSuccessBg: colorBgSuccess,
      colorWarningBg: colorBgWarning,
      colorInfoBg: colorBgInfo,

      colorErrorText: colorText,
      colorSuccessText: colorText,
      colorWarningText: colorText,
      colorInfoText: colorText,

      fontSizeIcon: 18,
    },
    Checkbox: {
      controlInteractiveSize: 20,
      borderRadiusLG: 4,
    },
    Radio: {
      radioSize: 20,
      dotSize: 10,
    },
    Collapse: {
      headerBg: colorWhite,
      borderRadius: 12,
    },
  },
}

export default primaryThemeDefault
